import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded'
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import AllInclusiveOutlinedIcon from '@mui/icons-material/AllInclusiveOutlined'
import { InputAdornment } from '@mui/material'
import dayjs from 'dayjs'
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useParams } from 'react-router'
import { useNavigate } from 'react-router'
import { ReceiptPrintDialogPaper } from 'src/clinic/components/ClinicManageBookingsPage/receiptPrinting/styledComponents'
import SortingHandler from 'src/common/tableRelated/SortingHandler'
import BaseUITabsComponent from 'src/common/tabs/BaseUITabs'
import MentorPastEventsListing from 'src/mentor/components/MentorEventsPage/components/bookingListing/MentorPastEventsListing'
import { ConfirmCancelSessionDialog } from 'src/mentor/components/MentorEventsPage/MentorEventsComponentsAndHelpers'
import SupportPatientActionsButton from 'src/supportPortal/components/bookings/SupportPatientActionsButton'
import SupportQuickActionsDialog from 'src/supportPortal/components/bookings/SupportQuickActionsDialog'
import SupportSessionCompletionReminderDialog from 'src/supportPortal/components/bookings/SupportSessionCompletionReminderDialog'
import { useSupportCall } from 'src/supportPortal/components/call/SupportCallProvider'
import { getUserRoles } from 'src/mentor/layout/utils'
import SupportBookedEventRescheduleDrawer from 'src/supportPortal/components/bookings/SupportBookedEventRescheduleDrawer'
import SupportBookedEventReassignDrawer from 'src/supportPortal/components/bookings/SupportBookedEventReassignDrawer'
import SupportBookedEventDetailDrawer from 'src/supportPortal/components/bookings/SupportBookedEventDetailDrawer'
import SupportBookedEventManageMenu from 'src/supportPortal/components/bookings/SupportBookedEventManageMenu'
import {
  SupportBillingMenu,
  SupportSessionMenu,
  getSessionMediaCapabilities,
} from 'src/supportPortal/components/bookings/SupportBookingCardActionMenus'
import SupportFeeDetailsDialog from 'src/supportPortal/components/bookings/SupportFeeDetailsDialog'
import SupportJoiningLinksDialog from 'src/supportPortal/components/bookings/SupportJoiningLinksDialog'
import SupportMarkAsPaidDialog from 'src/supportPortal/components/bookings/SupportMarkAsPaidDialog'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import SupportContactActions from 'src/supportPortal/components/contact/SupportContactActions'
import EngagementSettingsDialog from 'src/supportPortal/components/EngagementSettingsDialog'
import { canManageEngagement } from 'src/supportPortal/services/patientEngagementService'
import { fetchClinicBrandsWithLocations } from 'src/supportPortal/services/clinicWorkforceService'
import { searchSupportReassignPractitioners } from 'src/supportPortal/services/supportBookedEventActionsService'
import {
  canActOnSupportBookedEvent,
  canMarkSupportBookedEventPaid,
  canViewSupportBookings,
  canViewSupportVcAuditBookings,
  getAllowedSupportBookedEventTypeOptions,
  hasAnySupportRole,
  SUPPORT_BOOKED_EVENT_TYPE_OPTIONS_CLINIC_SUCCESS,
  canManageSupportBookedEventInvoices,
  canManageSupportRecordingPreferences,
} from 'src/supportPortal/access/accessMatrix'
import { BOOKING_PAYMENT_STATUSES } from 'src/consumer/constants'
import { getSPDataWithoutParams, postSPDataWithoutParams } from 'src/supportPortal/services/services'

const ReceiptPrintingComponent = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/receiptPrinting/ReceiptPrintingComponent')
)

const SORT_OPTIONS = [
  { label: 'Start Time', value: 'startTime' },
  { label: 'Booking Time', value: 'createdTime' },
]

// URL slug <-> event-type id for deep-linkable tabs (/booked-events/consultations etc.)
export const SUPPORT_EVENT_TYPE_SLUGS = {
  consultation: 'consultations',
  screening: 'screenings',
  'therapy-session': 'therapy-sessions',
  assessment: 'assessments',
}
const SUPPORT_EVENT_TYPE_BY_SLUG = Object.fromEntries(
  Object.entries(SUPPORT_EVENT_TYPE_SLUGS).map(([type, slug]) => [slug, type]),
)

const SESSION_STATUS_OPTIONS = [
  { value: 'not-started', label: 'Not started' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'finished', label: 'Finished' },
  { value: 'marked-completed', label: 'Marked completed' },
]

// Roles that can call /cc/support/events/actions/* (mirrors the backend controller gate).
const SUPPORT_BOOKING_ACTION_API_ROLES = [
  'ROLE_THERAPY_SUP_L2',
  'ROLE_THERAPY_SUP_L3',
  'ROLE_CLINIC_SUCCESS_MANAGER',
  'ROLE_CLINIC_SUCCESS_ADMIN',
  'ROLE_ADMIN',
]

const FILTER_CONTROL_SX = {
  minWidth: 168,
  '& .MuiInputBase-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    fontSize: 13,
    height: 40,
  },
  '& .MuiInputLabel-root': { fontSize: 12.5, color: '#94a3b8' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4f46e5' },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e2e8f0',
    transition: 'border-color 0.15s ease',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#4f46e5 !important',
    borderWidth: '1.5px',
  },
}

/**
 * Modern segmented control (grey track + lifted white selected segment) shared by the
 * Mode and quick-date filters.
 */
const SEGMENTED_GROUP_SX = {
  height: 40,
  p: '3px',
  gap: '2px',
  backgroundColor: '#f1f5f9',
  borderRadius: '10px',
  border: '1px solid #e2e8f0',
  '& .MuiToggleButtonGroup-grouped': {
    m: 0,
    border: 0,
    borderRadius: '7px !important',
  },
  '& .MuiToggleButton-root': {
    px: 1.4,
    py: 0,
    gap: 0.5,
    textTransform: 'none',
    fontSize: 12.5,
    fontWeight: 650,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    color: '#64748b',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.65)', color: '#334155' },
    '&.Mui-selected': {
      backgroundColor: '#ffffff',
      color: '#4338ca',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.1)',
      '&:hover': { backgroundColor: '#ffffff' },
    },
  },
}

/** Labeled cluster inside the filter bar so ten controls read as five groups, not soup. */
function FilterGroup({ label, children }) {
  return (
    <Stack spacing={0.6}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          color: '#94a3b8',
          lineHeight: 1,
          pl: 0.25,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {children}
      </Stack>
    </Stack>
  )
}

/**
 * Support-scope filters (practitioner / clinic / meeting mode / session status) that the
 * generic in-listing filter panel doesn't cover. Values feed the listing via defaultFilters
 * and map 1:1 onto SupportBookedEventInfoSearch params.
 */
function SupportBookingsFilterBar({ value, onChange, showPractitionerFilter, eventType }) {
  const [brands, setBrands] = useState(null) // null = loading/unavailable, [] = none
  const [practitionerOptions, setPractitionerOptions] = useState([])
  const [practitionerInput, setPractitionerInput] = useState('')
  const [practitionerSearching, setPractitionerSearching] = useState(false)
  const searchTimerRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    async function loadBrands() {
      const brandList = await fetchClinicBrandsWithLocations()
      if (!cancelled) {
        setBrands(Array.isArray(brandList) ? brandList : [])
      }
    }
    void loadBrands()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!showPractitionerFilter) {
      return undefined
    }
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }
    searchTimerRef.current = setTimeout(async () => {
      setPractitionerSearching(true)
      // Scope the picker to the practitioners approved for the active tab's event type
      // (consultation -> doctors, therapy -> therapists, ...). The backend applies the
      // role filter; older backends ignore the extra param and return everyone.
      const practitioners = await searchSupportReassignPractitioners(
        practitionerInput?.trim() || '',
        eventType,
      )
      setPractitionerOptions(practitioners)
      setPractitionerSearching(false)
    }, 400)
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [practitionerInput, showPractitionerFilter, eventType])

  const selectedBrand = useMemo(
    () => (brands || []).find((brand) => brand.id === value.clinicBrandId) || null,
    [brands, value.clinicBrandId],
  )

  const activeFilterCount =
    ['mentorId', 'clinicBrandId', 'clinicLocationId', 'meetingSrc', 'startTime', 'endTime'].filter(
      (key) => Boolean(value[key]),
    ).length +
    (value.sessionStatusIds?.length ? 1 : 0) +
    (value.statusIds?.length ? 1 : 0) +
    (value.paymentStatusIds?.length ? 1 : 0)

  const setFilterValue = (patch) => {
    onChange({ ...value, ...patch })
  }

  const applyQuickDate = (dayOffset) => {
    const day = dayjs().add(dayOffset, 'day')
    setFilterValue({
      startTime: day.startOf('day').toISOString(),
      endTime: day.endOf('day').toISOString(),
    })
  }
  const quickDateActive = (dayOffset) => {
    if (!value.startTime || !value.endTime) {
      return false
    }
    const day = dayjs().add(dayOffset, 'day')
    return (
      dayjs(value.startTime).isSame(day.startOf('day'), 'minute') &&
      dayjs(value.endTime).isSame(day.endOf('day'), 'minute')
    )
  }
  const activeQuickDateValue = (() => {
    const matchedOffset = [-1, 0, 1].find((offset) => quickDateActive(offset))
    return matchedOffset === undefined ? null : String(matchedOffset)
  })()

  return (
    <Stack
      direction="row"
      spacing={2.25}
      alignItems="flex-start"
      sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
    >
      {showPractitionerFilter ? (
        <FilterGroup label="Practitioner">
        <Autocomplete
          size="small"
          sx={{ ...FILTER_CONTROL_SX, minWidth: 230 }}
          options={practitionerOptions}
          value={value.mentorOption || null}
          onChange={(event, nextValue) =>
            setFilterValue({ mentorId: nextValue?.id || undefined, mentorOption: nextValue || undefined })
          }
          inputValue={practitionerInput}
          onInputChange={(event, nextValue) => setPractitionerInput(nextValue)}
          getOptionLabel={(option) => option?.name || option?.slug || ''}
          isOptionEqualToValue={(option, optionValue) => option?.id === optionValue?.id}
          filterOptions={(items) => items}
          loading={practitionerSearching}
          noOptionsText={practitionerInput ? 'No matches' : 'Type to search'}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Practitioner"
              placeholder="Search by name or email"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <PersonSearchOutlinedIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        </FilterGroup>
      ) : null}

      {brands && brands.length > 0 ? (
        <FilterGroup label="Clinic">
        <TextField
          select
          size="small"
          label="Clinic brand"
          sx={FILTER_CONTROL_SX}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <StorefrontOutlinedIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
          value={value.clinicBrandId || ''}
          onChange={(event) =>
            setFilterValue({
              clinicBrandId: event.target.value || undefined,
              clinicLocationId: undefined,
            })
          }
        >
          <MenuItem value="">All brands</MenuItem>
          {brands.map((brand) => (
            <MenuItem key={brand.id} value={brand.id}>
              {brand.title}
            </MenuItem>
          ))}
        </TextField>
        {selectedBrand && selectedBrand.locations?.length > 0 ? (
        <TextField
          select
          size="small"
          label="Location"
          sx={FILTER_CONTROL_SX}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PlaceOutlinedIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
          value={value.clinicLocationId || ''}
          onChange={(event) => setFilterValue({ clinicLocationId: event.target.value || undefined })}
        >
          <MenuItem value="">All locations</MenuItem>
          {selectedBrand.locations.map((location) => (
            <MenuItem key={location.id} value={location.id}>
              {location.title || location.name}
            </MenuItem>
          ))}
        </TextField>
        ) : null}
        </FilterGroup>
      ) : null}

      <FilterGroup label="Mode">
      <ToggleButtonGroup
        size="small"
        exclusive
        value={value.meetingSrc || 'all'}
        onChange={(event, nextValue) => {
          if (nextValue !== null) {
            setFilterValue({ meetingSrc: nextValue === 'all' ? undefined : nextValue })
          }
        }}
        sx={SEGMENTED_GROUP_SX}
      >
        <ToggleButton value="all">
          <AllInclusiveOutlinedIcon sx={{ fontSize: 15 }} /> All
        </ToggleButton>
        <ToggleButton value="aumhum">
          <VideocamOutlinedIcon sx={{ fontSize: 16 }} /> Video Call
        </ToggleButton>
        <ToggleButton value="offline">
          <MeetingRoomOutlinedIcon sx={{ fontSize: 16 }} /> In-Clinic
        </ToggleButton>
      </ToggleButtonGroup>
      </FilterGroup>

      <FilterGroup label="Status">
      <TextField
        select
        size="small"
        label="Session status"
        sx={{ ...FILTER_CONTROL_SX, minWidth: 160 }}
        SelectProps={{
          multiple: true,
          renderValue: (selected) =>
            selected.length === 0
              ? 'Any'
              : SESSION_STATUS_OPTIONS.filter((option) => selected.includes(option.value))
                  .map((option) => option.label)
                  .join(', '),
        }}
        value={value.sessionStatusIds || []}
        onChange={(event) =>
          setFilterValue({
            sessionStatusIds:
              event.target.value && event.target.value.length > 0 ? event.target.value : undefined,
          })
        }
      >
        {SESSION_STATUS_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label="Booking status"
        sx={{ ...FILTER_CONTROL_SX, minWidth: 160 }}
        SelectProps={{
          multiple: true,
          renderValue: (selected) => (selected.length === 0 ? 'Any' : selected.join(', ')),
        }}
        value={value.statusIds || []}
        onChange={(event) =>
          setFilterValue({
            statusIds:
              event.target.value && event.target.value.length > 0 ? event.target.value : undefined,
          })
        }
      >
        <MenuItem value="booked">Booked</MenuItem>
        <MenuItem value="booking-initiated">Booking initiated</MenuItem>
        <MenuItem value="cancelled">Cancelled</MenuItem>
        <MenuItem value="completed">Completed</MenuItem>
      </TextField>

      <TextField
        select
        size="small"
        label="Payment"
        sx={{ ...FILTER_CONTROL_SX, minWidth: 120 }}
        SelectProps={{
          multiple: true,
          renderValue: (selected) => (selected.length === 0 ? 'Any' : selected.join(', ')),
        }}
        value={value.paymentStatusIds || []}
        onChange={(event) =>
          setFilterValue({
            paymentStatusIds:
              event.target.value && event.target.value.length > 0 ? event.target.value : undefined,
          })
        }
      >
        <MenuItem value="paid">Paid</MenuItem>
        <MenuItem value="pending">Pending</MenuItem>
      </TextField>
      </FilterGroup>

      <FilterGroup label="Date">
      <ToggleButtonGroup
        size="small"
        exclusive
        value={activeQuickDateValue}
        onChange={(event, nextValue) => {
          if (nextValue === null) {
            setFilterValue({ startTime: undefined, endTime: undefined })
          } else {
            applyQuickDate(Number(nextValue))
          }
        }}
        sx={SEGMENTED_GROUP_SX}
      >
        <ToggleButton value="-1">Yesterday</ToggleButton>
        <ToggleButton value="0">Today</ToggleButton>
        <ToggleButton value="1">Tomorrow</ToggleButton>
      </ToggleButtonGroup>
      <TextField
        size="small"
        type="date"
        label="From"
        sx={{ ...FILTER_CONTROL_SX, minWidth: 145 }}
        InputLabelProps={{ shrink: true }}
        value={value.startTime ? dayjs(value.startTime).format('YYYY-MM-DD') : ''}
        onChange={(event) =>
          setFilterValue({
            startTime: event.target.value
              ? dayjs(event.target.value).startOf('day').toISOString()
              : undefined,
          })
        }
      />
      <TextField
        size="small"
        type="date"
        label="To"
        sx={{ ...FILTER_CONTROL_SX, minWidth: 145 }}
        InputLabelProps={{ shrink: true }}
        value={value.endTime ? dayjs(value.endTime).format('YYYY-MM-DD') : ''}
        onChange={(event) =>
          setFilterValue({
            endTime: event.target.value
              ? dayjs(event.target.value).endOf('day').toISOString()
              : undefined,
          })
        }
      />
      </FilterGroup>

      {activeFilterCount > 0 ? (
        <Button
          size="small"
          startIcon={<FilterAltOffOutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={() => onChange({})}
          sx={{
            alignSelf: 'flex-end',
            height: 40,
            px: 1.5,
            borderRadius: '10px',
            textTransform: 'none',
            fontSize: 12.5,
            fontWeight: 650,
            color: '#64748b',
            backgroundColor: 'rgba(148, 163, 184, 0.12)',
            '&:hover': { backgroundColor: 'rgba(148, 163, 184, 0.2)', color: '#475569' },
          }}
        >
          Clear ({activeFilterCount})
        </Button>
      ) : null}
    </Stack>
  )
}

function mergeEventTypeOptions(baseOptions = [], extraOptions = []) {
  const merged = []
  ;[...baseOptions, ...extraOptions].forEach((option) => {
    if (!option?.id || merged.some((existing) => existing.id === option.id)) {
      return
    }
    merged.push(option)
  })
  return merged
}

function LegacyBookedEventsView({
  showHeader = true,
  eventTypeOptions = [],
  hasVcAuditAccess = false,
  hasRecordingPreferenceAccess = false,
  hasInvoiceAccess = false,
  heading = 'Bookings & Appointments',
  helperText = '',
  defaultShowOnlyPaidEvents = true,
  defaultFilters = {},
  showPaymentPendingToggle = true,
  controlledEventType,
  onEventTypeChange,
}) {
  const [internalEventType, setInternalEventType] = useState(
    eventTypeOptions[0]?.id || null,
  )
  const isControlledEventType = controlledEventType !== undefined
  const selectedEventType = isControlledEventType ? controlledEventType : internalEventType
  const setSelectedEventType = (nextType) => {
    if (isControlledEventType) {
      onEventTypeChange?.(nextType)
    } else {
      setInternalEventType(nextType)
    }
  }
  const navigate = useNavigate()
  const [sortingHandler, setSortingHandler] = useState(null)
  const [showOnlyPaidEvents, setShowOnlyPaidEvents] = useState(defaultShowOnlyPaidEvents)
  const [liveNow, setLiveNow] = useState(false)
  const LIVE_WINDOW_MINUTES = 30
  const [engagementDialogOpen, setEngagementDialogOpen] = useState(false)
  const [selectedEventForEngagement, setSelectedEventForEngagement] = useState(null)
  const [rescheduleDrawerBooking, setRescheduleDrawerBooking] = useState(null)
  const [reassignDrawerBooking, setReassignDrawerBooking] = useState(null)
  const [markPaidDialogBooking, setMarkPaidDialogBooking] = useState(null)
  const [detailDrawerBooking, setDetailDrawerBooking] = useState(null)
  const [joiningLinksBooking, setJoiningLinksBooking] = useState(null)
  const [feeDetailsBooking, setFeeDetailsBooking] = useState(null)
  const [quickActionsBooking, setQuickActionsBooking] = useState(null)
  const [completionReminderBooking, setCompletionReminderBooking] = useState(null)
  const [cancelDialogBooking, setCancelDialogBooking] = useState(null)
  const { openCall } = useSupportCall()
  const [supportFilters, setSupportFilters] = useState({})
  const [invoiceDialogState, setInvoiceDialogState] = useState({
    open: false,
    bookedEvent: null,
    openSendDialogOnReady: false,
  })
  const hasEngagementPermission = useMemo(() => canManageEngagement(), [])
  const userRoles = useMemo(() => getUserRoles() || [], [])
  const canUseBookingActionsApi = useMemo(
    () => hasAnySupportRole(userRoles, SUPPORT_BOOKING_ACTION_API_ROLES),
    [userRoles],
  )
  const listingRef = useRef(null)

  const refreshListing = useCallback(() => {
    listingRef.current?.resetData?.()
  }, [])

  // Support-scope filters merged over the page-mode defaults; the listing forwards the extra
  // keys (mentorId / clinicBrandId / clinicLocationId / meetingSrc / sessionStatusIds) to the
  // paginated-unified API. mentorOption is picker-only UI state and stripped here.
  const listingDefaultFilters = useMemo(() => {
    const merged = { ...defaultFilters }
    ;[
      'mentorId',
      'clinicBrandId',
      'clinicLocationId',
      'meetingSrc',
      'sessionStatusIds',
      'statusIds',
      'paymentStatusIds',
      'startTime',
      'endTime',
    ].forEach((key) => {
      if (supportFilters[key] !== undefined && supportFilters[key] !== null) {
        merged[key] = supportFilters[key]
      }
    })
    if (liveNow) {
      merged.liveNow = true
      merged.liveWindowMinutes = LIVE_WINDOW_MINUTES
    }
    return merged
  }, [defaultFilters, supportFilters, liveNow])

  useEffect(() => {
    if (isControlledEventType) {
      return
    }
    if (
      eventTypeOptions.length > 0 &&
      !eventTypeOptions.some((option) => option.id === internalEventType)
    ) {
      setInternalEventType(eventTypeOptions[0].id)
    } else if (eventTypeOptions.length === 0 && internalEventType !== null) {
      setInternalEventType(null)
    }
  }, [eventTypeOptions, internalEventType, isControlledEventType])

  const handleEngagementClick = (bookedEvent) => {
    setSelectedEventForEngagement(bookedEvent)
    setEngagementDialogOpen(true)
  }

  const closeInvoiceDialog = useCallback(() => {
    setInvoiceDialogState({
      open: false,
      bookedEvent: null,
      openSendDialogOnReady: false,
    })
  }, [])

  const openInvoiceDialog = useCallback((bookedEvent, { openSendDialogOnReady = false } = {}) => {
    if (!bookedEvent?.id) {
      return
    }

    setInvoiceDialogState({
      open: true,
      bookedEvent,
      openSendDialogOnReady,
    })
  }, [])

  const receiptCurrentClinic = useMemo(() => {
    const eventData = invoiceDialogState.bookedEvent
    if (!eventData) return null
    const title =
      eventData?.clinicLocation?.title ||
      eventData?.clinicLocation?.name ||
      eventData?.clinicName ||
      eventData?.clinicLocationName ||
      eventData?.clinicLocationTitle
    const address =
      eventData?.clinicLocation?.address ||
      eventData?.address ||
      eventData?.clinicAddress ||
      eventData?.clinicLocationAddress
    const mobile =
      eventData?.clinicLocation?.mobile ||
      eventData?.mobile
    const clinicBrandName =
      eventData?.clinicBrandName ||
      eventData?.clinicBrandData?.brandName ||
      eventData?.clinicBrandData?.name ||
      eventData?.clinicBrandData?.title
    const clinicBrandShortName = eventData?.clinicBrandData?.shortName
    const brandName =
      eventData?.clinicBrandData?.brandName ||
      eventData?.clinicBrandData?.name ||
      eventData?.clinicBrandData?.title

    return {
      clinicBrandName,
      clinicBrandShortName,
      brandName,
      title,
      clinicLocationName: title,
      location: address != null ? { address } : null,
      address,
      mobile,
    }
  }, [invoiceDialogState.bookedEvent])

  return (
    <>
      {showHeader ? <SupportHeader heading={heading} /> : null}
      {helperText ? (
        <Box sx={{ maxWidth: '1200px', margin: '0 auto', width: '100%', px: 1.5, pt: 1.5 }}>
          <Alert severity="info">{helperText}</Alert>
        </Box>
      ) : null}
      <Box sx={{ maxWidth: '1360px', margin: '0 auto', width: '100%', px: 1.5, boxSizing: 'border-box' }}>
        <Paper
          elevation={0}
          sx={{
            mt: 1.5,
            mb: 1.25,
            borderRadius: '12px',
            border: '1px solid rgba(226, 232, 240, 0.9)',
            backgroundColor: '#ffffff',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1.5}
            sx={{ px: { xs: 1.5, sm: 2 }, py: 0.9, flexWrap: 'wrap', rowGap: 0.75 }}
          >
            <Box
              sx={{
                minWidth: 0,
                // BaseUITabs ships an emerald indicator pill; align it with the page's
                // indigo accent without touching the shared component.
                '& .support-bookings-tab-indicator': {
                  backgroundColor: 'rgba(79, 70, 229, 0.09) !important',
                  boxShadow: 'none !important',
                },
                '& [data-state="active"]': {
                  color: '#312e81 !important',
                  fontWeight: 650,
                },
              }}
            >
              {eventTypeOptions.length > 0 ? (
                <BaseUITabsComponent
                  value={selectedEventType}
                  onChange={setSelectedEventType}
                  tabs={eventTypeOptions}
                  getTabLabel={(tab) => tab?.title}
                  getTabId={(tab) => tab?.id}
                  unselectedColor="#64748b"
                  selectedColor="#312e81"
                  hoverItemColor="#0f172a"
                  classNames={{ indicator: 'support-bookings-tab-indicator' }}
                />
              ) : null}
            </Box>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Button
                size="small"
                onClick={() => setLiveNow((value) => !value)}
                startIcon={
                  <FiberManualRecordIcon
                    sx={{
                      fontSize: 12,
                      color: liveNow ? '#ffffff' : '#ef4444',
                      animation: liveNow ? 'support-live-toggle-pulse 1.4s ease-in-out infinite' : 'none',
                      '@keyframes support-live-toggle-pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.35 },
                      },
                    }}
                  />
                }
                sx={{
                  height: 34,
                  px: 1.4,
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontSize: 12.5,
                  fontWeight: 700,
                  border: `1px solid ${liveNow ? '#dc2626' : 'rgba(203, 213, 225, 0.9)'}`,
                  color: liveNow ? '#ffffff' : '#475569',
                  backgroundColor: liveNow ? '#ef4444' : '#ffffff',
                  '&:hover': {
                    backgroundColor: liveNow ? '#dc2626' : '#fef2f2',
                    borderColor: '#ef4444',
                  },
                }}
              >
                Live now
              </Button>
              {showPaymentPendingToggle ? (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showOnlyPaidEvents}
                      onChange={() => setShowOnlyPaidEvents(!showOnlyPaidEvents)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                      Hide payment pending
                    </Typography>
                  }
                  sx={{ mr: 0 }}
                />
              ) : null}
              <SortingHandler
                sortValue={sortingHandler}
                setSortValue={setSortingHandler}
                getOptionLabel={(option) => option?.label}
                getOptionValue={(option) => option?.value}
                sortOptions={SORT_OPTIONS}
                outerDivStyle={{
                  color: '#475569',
                  fontSize: '14px',
                  alignItems: 'center',
                }}
                buttonColor="primary"
                buttonVariant="outlined"
              />
            </Stack>
          </Stack>
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: 1.5,
              borderTop: '1px solid rgba(226, 232, 240, 0.8)',
              backgroundColor: '#fafbfd',
            }}
          >
            <SupportBookingsFilterBar
              value={supportFilters}
              onChange={setSupportFilters}
              showPractitionerFilter={canUseBookingActionsApi}
              eventType={selectedEventType}
            />
          </Box>
        </Paper>
      </Box>
      <div style={{ maxWidth: '1360px', margin: '0 auto', width: '100%', padding: '0 12px', boxSizing: 'border-box' }}>
        <MentorPastEventsListing
          ref={listingRef}
          key={`${selectedEventType || 'all'}-${showOnlyPaidEvents}`}
          listingApiSignature="/api/secure/cc/support/events/all/paginated-unified"
          showMentorName
          hidePortalVoiceCallAction
          cancelAppointmentApiSignature="/api/secure/cc/support/events/actions/cancel"
          skipCancelCheckInGuard
          listOfPaymentLinksApiSignature="/api/secure/cc/support/events/user-payment-link/get"
          eventType={selectedEventType || undefined}
          sortingHandler={sortingHandler}
          showOnlyPaidEvents={showOnlyPaidEvents}
          defaultFilters={listingDefaultFilters}
          skipCommunicationTargetsPrefetch
          hideFiltersPanel
          logsApiEndpoint="/api/secure/cc/support/events/logs/get"
          filterByBookedStatus={null}
          eventTypeOptions={eventTypeOptions}
          onRescheduleBooking={(bookedEvent) => setRescheduleDrawerBooking(bookedEvent)}
          onEngagementClick={hasEngagementPermission ? handleEngagementClick : undefined}
          onSupportTimeSlotClick={(bookedEvent) => setQuickActionsBooking(bookedEvent)}
          onSupportPhoneClick={(bookedEvent) => openCall({ booking: bookedEvent })}
          bookingCardLayout="support"
          renderActionsHeaderTrailing={(bookedEvent) => (
            <SupportContactActions
              patientId={bookedEvent?.patientId}
              practitionerId={bookedEvent?.mentorId}
              bookedEventId={bookedEvent?.id}
              clinicBrandId={bookedEvent?.clinicBrandId}
              clinicId={bookedEvent?.clinicId}
              targetRole="patient"
              eventType={bookedEvent?.eventType || selectedEventType}
              title=""
              compactIconStrip
              skipCapabilitiesPrefetch
              uiSurface="support-booked-events-strip"
              sx={{
                px: 0.45,
                py: 0.3,
                borderRadius: 999,
                border: '1px solid rgba(203, 213, 225, 0.9)',
                background: '#f8fafc',
              }}
            />
          )}
          renderViewActionItems={(bookedEvent, { BookingActionButton, supportContext }) => {
            if (!bookedEvent?.id) {
              return null
            }

            const paymentLinksHandler =
              supportContext?.paymentLinks?.onClick || supportContext?.blockedPaymentLinks?.onClick
            // Real-time audit ("Watch Live") is role-gated: getSessionMediaCapabilities only
            // returns canPlayLive when the viewer has VC-audit access and the session is live.
            const liveMediaCapabilities = getSessionMediaCapabilities(
              bookedEvent,
              hasVcAuditAccess,
              hasRecordingPreferenceAccess,
            )

            const items = []

            if (liveMediaCapabilities.canPlayLive) {
              items.push(
                <BookingActionButton
                  key="support-watch-live"
                  label={bookedEvent?.vcAuditLiveBadgeLabel || 'Watch Live'}
                  onClick={() =>
                    navigate(`/support-home/booked-events/${bookedEvent.id}/live`, {
                      state: { from: '/support-home/booked-events' },
                    })
                  }
                  startIcon={<PlayCircleFilledRoundedIcon sx={{ fontSize: 16 }} />}
                  title="Audit this live session in real time"
                  style={{
                    color: '#ffffff',
                    backgroundColor: '#ef4444',
                    borderColor: '#ef4444',
                  }}
                />,
              )
            }

            items.push(
              <BookingActionButton
                key="support-details"
                label="Details"
                onClick={() => setDetailDrawerBooking(bookedEvent)}
                tone="view"
                startIcon={<InfoOutlinedIcon sx={{ fontSize: 15 }} />}
                title="Full booking details: schedule, payment, links, history and support activity"
              />,
            )

            if (supportContext?.supportReport) {
              items.push(
                <BookingActionButton
                  key="support-report"
                  label={supportContext.supportReport.label || 'Report'}
                  onClick={supportContext.supportReport.onClick}
                  tone="report"
                  startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 15 }} />}
                  title={supportContext.supportReport.title || 'Report for this session'}
                />,
              )
            }

            // Participant logs are surfaced as a first-class card button (not buried in the
            // Session menu) so they're one tap away once a session is under way — for every
            // mode, in-clinic and video alike.
            if (supportContext?.participantLogs) {
              items.push(
                <BookingActionButton
                  key="support-participant-logs"
                  label="Participant Logs"
                  onClick={() => supportContext.participantLogs.onClick()}
                  tone="logs"
                  startIcon={<FactCheckOutlinedIcon sx={{ fontSize: 15 }} />}
                  title="Who joined this session, and when"
                />,
              )
            }

            // The mentor session-end "patient actions" set (Send Prescription / Book Slot / Send
            // Payment Link / Bill Patient / Send Google Review / Send Attachment), grouped behind one
            // common dropdown. supportContext.supportReport tells us when the session's clinical
            // document exists so the (report-type-aware) "Send …" action can offer to deliver it.
            items.push(
              <SupportPatientActionsButton
                key="support-patient-actions"
                bookedEvent={bookedEvent}
                supportContext={supportContext}
              />,
            )

            items.push(
              <SupportBillingMenu
                key="support-billing"
                bookedEvent={bookedEvent}
                onFeeDetails={(eventForFee) => setFeeDetailsBooking(eventForFee)}
                onPaymentLinks={paymentLinksHandler ? () => paymentLinksHandler() : undefined}
                canManageInvoices={hasInvoiceAccess}
                onGenerateInvoice={(eventForInvoice) => openInvoiceDialog(eventForInvoice)}
                onSendInvoice={(eventForInvoice) =>
                  openInvoiceDialog(eventForInvoice, { openSendDialogOnReady: true })
                }
                canMarkPaid={canMarkSupportBookedEventPaid(userRoles, bookedEvent?.eventType)}
                onMarkPaid={(eventForPayment) => setMarkPaidDialogBooking(eventForPayment)}
              />,
            )

            return items
          }}
          renderManageSessionActionItems={(bookedEvent, { supportContext }) => {
            if (!bookedEvent?.id) {
              return null
            }
            const canManageThisBooking =
              canUseBookingActionsApi && canActOnSupportBookedEvent(userRoles, bookedEvent?.eventType)

            return [
              <SupportSessionMenu
                key="support-session"
                bookedEvent={bookedEvent}
                hasVcAuditAccess={hasVcAuditAccess}
                hasRecordingPreferenceAccess={hasRecordingPreferenceAccess}
                onJoiningLinks={(eventForLinks) => setJoiningLinksBooking(eventForLinks)}
                onActionSuccess={refreshListing}
              />,
              <SupportBookedEventManageMenu
                key="support-manage-menu"
                bookedEvent={bookedEvent}
                supportContext={supportContext}
                canManageActions={canManageThisBooking}
                onReassign={(eventForReassign) => setReassignDrawerBooking(eventForReassign)}
                onActionSuccess={refreshListing}
              />,
            ]
          }}
        />
      </div>

      {hasEngagementPermission && (
        <EngagementSettingsDialog
          open={engagementDialogOpen}
          onClose={() => {
            setEngagementDialogOpen(false)
            setSelectedEventForEngagement(null)
          }}
          targetType="booked-event"
          target={selectedEventForEngagement}
          onSuccess={() => {}}
        />
      )}

      <SupportBookedEventRescheduleDrawer
        open={Boolean(rescheduleDrawerBooking)}
        bookedEvent={rescheduleDrawerBooking}
        onClose={() => setRescheduleDrawerBooking(null)}
        onSuccess={() => {
          setRescheduleDrawerBooking(null)
          refreshListing()
        }}
      />

      <SupportBookedEventReassignDrawer
        open={Boolean(reassignDrawerBooking)}
        bookedEvent={reassignDrawerBooking}
        onClose={() => setReassignDrawerBooking(null)}
        onSuccess={() => {
          setReassignDrawerBooking(null)
          refreshListing()
        }}
      />

      <SupportMarkAsPaidDialog
        open={Boolean(markPaidDialogBooking)}
        bookedEvent={markPaidDialogBooking}
        onClose={() => setMarkPaidDialogBooking(null)}
        onSuccess={() => {
          setMarkPaidDialogBooking(null)
          refreshListing()
        }}
      />

      <SupportBookedEventDetailDrawer
        open={Boolean(detailDrawerBooking)}
        bookedEvent={detailDrawerBooking}
        onClose={() => setDetailDrawerBooking(null)}
      />

      <SupportJoiningLinksDialog
        open={Boolean(joiningLinksBooking)}
        bookedEvent={joiningLinksBooking}
        onClose={() => setJoiningLinksBooking(null)}
      />

      <SupportFeeDetailsDialog
        open={Boolean(feeDetailsBooking)}
        bookedEvent={feeDetailsBooking}
        onClose={() => setFeeDetailsBooking(null)}
      />

      <SupportQuickActionsDialog
        open={Boolean(quickActionsBooking)}
        bookedEvent={quickActionsBooking}
        onClose={() => setQuickActionsBooking(null)}
        hasVcAuditAccess={hasVcAuditAccess}
        hasRecordingPreferenceAccess={hasRecordingPreferenceAccess}
        canManageActions={
          quickActionsBooking
            ? canUseBookingActionsApi &&
              canActOnSupportBookedEvent(userRoles, quickActionsBooking?.eventType)
            : false
        }
        canMarkPaid={
          quickActionsBooking
            ? canMarkSupportBookedEventPaid(userRoles, quickActionsBooking?.eventType)
            : false
        }
        onDetails={(booking) => setDetailDrawerBooking(booking)}
        onReschedule={(booking) => setRescheduleDrawerBooking(booking)}
        onReassign={(booking) => setReassignDrawerBooking(booking)}
        onMarkPaid={(booking) => setMarkPaidDialogBooking(booking)}
        onFeeDetails={(booking) => setFeeDetailsBooking(booking)}
        onWatchLive={(booking) =>
          navigate(`/support-home/booked-events/${booking.id}/live`, {
            state: { from: '/support-home/booked-events' },
          })
        }
        onCancel={(booking) => setCancelDialogBooking(booking)}
        onRemindComplete={(booking) => setCompletionReminderBooking(booking)}
        onActionSuccess={refreshListing}
      />

      <SupportSessionCompletionReminderDialog
        open={Boolean(completionReminderBooking)}
        booking={completionReminderBooking}
        onClose={() => setCompletionReminderBooking(null)}
        onSent={refreshListing}
      />

      {cancelDialogBooking ? (
        <ConfirmCancelSessionDialog
          open={Boolean(cancelDialogBooking)}
          closeFunc={() => setCancelDialogBooking(null)}
          sessionData={cancelDialogBooking}
          afterCancelSuccess={() => {
            setCancelDialogBooking(null)
            refreshListing()
          }}
          cancelAppointmentApiSignature="/api/secure/cc/support/events/actions/cancel"
        />
      ) : null}

      <Dialog
        open={invoiceDialogState.open}
        PaperComponent={ReceiptPrintDialogPaper}
        onClose={closeInvoiceDialog}
        className="receipt-print-dialog"
      >
        <Suspense
          fallback={
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          }
        >
          <ReceiptPrintingComponent
            bookedEvent={invoiceDialogState.bookedEvent}
            bookedEventId={invoiceDialogState.bookedEvent?.id}
            patientSearchApiSignature="/api/secure/cc/support/patient/search"
            closeDialog={closeInvoiceDialog}
            fetchDataWithoutParams={getSPDataWithoutParams}
            currentClinic={receiptCurrentClinic}
            sendReceiptApiSignature="/api/secure/cc/support/receipt/send"
            postDataForSendReceipt={postSPDataWithoutParams}
            documentKindLabel="Invoice"
            openSendDialogOnReady={invoiceDialogState.openSendDialogOnReady}
          />
        </Suspense>
      </Dialog>
    </>
  )
}

export default function BookedEvents({ pageMode = 'default' }) {
  const { eventTypeSlug } = useParams()
  const navigate = useNavigate()
  const userRoles = getUserRoles()
  const hasVcAuditAccess = canViewSupportVcAuditBookings(userRoles)
  const hasSupportBookingsAccess = canViewSupportBookings(userRoles)
  const hasRecordingPreferenceAccess = canManageSupportRecordingPreferences(userRoles)
  const hasInvoiceAccess = canManageSupportBookedEventInvoices(userRoles)
  const allowedSupportEventTypes = useMemo(() => {
    const baseOptions = getAllowedSupportBookedEventTypeOptions(userRoles)
    if (!hasVcAuditAccess) {
      return baseOptions
    }
    return mergeEventTypeOptions(baseOptions, SUPPORT_BOOKED_EVENT_TYPE_OPTIONS_CLINIC_SUCCESS)
  }, [hasVcAuditAccess, userRoles])
  const pageConfig = useMemo(() => {
    if (pageMode === 'payment-links') {
      return {
        heading: 'Payment Links',
        helperText: 'Review payment-pending bookings and open their payment links from one place.',
        defaultShowOnlyPaidEvents: false,
        defaultFilters: {
          paymentStatusIds: [BOOKING_PAYMENT_STATUSES.PENDING],
        },
        showPaymentPendingToggle: false,
      }
    }

    return {
      heading: 'Bookings & Appointments',
      helperText: '',
      defaultShowOnlyPaidEvents: true,
      defaultFilters: {},
      showPaymentPendingToggle: true,
    }
  }, [pageMode])

  // URL-driven tab: the slug wins when the role can see it; otherwise fall back to the
  // role's first allowed event type (the role-appropriate default) and canonicalize the URL.
  const slugEventType = SUPPORT_EVENT_TYPE_BY_SLUG[eventTypeSlug] || null
  const resolvedEventType = useMemo(() => {
    if (slugEventType && allowedSupportEventTypes.some((option) => option.id === slugEventType)) {
      return slugEventType
    }
    return allowedSupportEventTypes[0]?.id || null
  }, [allowedSupportEventTypes, slugEventType])

  useEffect(() => {
    if (pageMode !== 'default' || !resolvedEventType) {
      return
    }
    const canonicalSlug = SUPPORT_EVENT_TYPE_SLUGS[resolvedEventType]
    if (canonicalSlug && eventTypeSlug !== canonicalSlug) {
      navigate(`/support-home/booked-events/${canonicalSlug}`, { replace: true })
    }
  }, [eventTypeSlug, navigate, pageMode, resolvedEventType])

  if (!hasVcAuditAccess && !hasSupportBookingsAccess) {
    return <Navigate to="/support-home" replace />
  }

  return (
    <LegacyBookedEventsView
      showHeader
      controlledEventType={pageMode === 'default' ? resolvedEventType : undefined}
      onEventTypeChange={
        pageMode === 'default'
          ? (nextType) => {
              const nextSlug = SUPPORT_EVENT_TYPE_SLUGS[nextType]
              if (nextSlug) {
                navigate(`/support-home/booked-events/${nextSlug}`)
              }
            }
          : undefined
      }
      eventTypeOptions={allowedSupportEventTypes}
      hasVcAuditAccess={hasVcAuditAccess}
      hasRecordingPreferenceAccess={hasRecordingPreferenceAccess}
      hasInvoiceAccess={hasInvoiceAccess}
      heading={pageConfig.heading}
      helperText={pageConfig.helperText}
      defaultShowOnlyPaidEvents={pageConfig.defaultShowOnlyPaidEvents}
      defaultFilters={pageConfig.defaultFilters}
      showPaymentPendingToggle={pageConfig.showPaymentPendingToggle}
    />
  )
}
